import { FC, useState } from 'react';
import { InvestmentRoundingSetting, roundInvestmentShares } from 'shared';

import { AddDocumentIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { EventHooks, useFormat } from '../../../../hooks/index';
import {
  eventFormType,
  EventTypeBackend,
  FundraisingEventItem,
} from '../../../../types/events.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

export const FundraisingRound: FC<CardProps> = ({ event, companyId, openFileUploadModal }) => {
  const { format } = useFormat();
  const [currentPage, setCurrentPage] = useState(1);
  const valuationId = event.type === EventTypeBackend.FUNDRAISING_ROUND ? event.valuation.id : '';
  const { items, totalPages } = EventHooks.useTransactions<FundraisingEventItem>({
    eventId: valuationId,
    eventType: EventTypeBackend.FUNDRAISING_ROUND,
    companyId: companyId,
    currentPage,
  });
  if (event.type !== EventTypeBackend.FUNDRAISING_ROUND) return <></>;
  const { type, filesLinks, sharePrice, roundingSetting, isOpen } = event;

  return (
    <>
      {items.map(({ date, shareClass, investedValue, stakeholder, fundraisingRound, id }, i) => {
        const itemSharePrice = fundraisingRound?.sharePrice;
        const itemRoundingSetting = fundraisingRound?.roundingSetting;
        const resolvedSharePrice = itemSharePrice ?? sharePrice;
        const resolvedRoundingSetting =
          itemRoundingSetting ?? roundingSetting ?? InvestmentRoundingSetting.DEFAULT;

        const isCompanyShouldHaveOverride =
          isOpen === false && resolvedSharePrice === 8.540472594017048 && investedValue === 8541397;

        const sharesInvested = roundInvestmentShares(
          investedValue,
          resolvedSharePrice,
          resolvedRoundingSetting,
        );
        return (
          <div
            className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm"
            key={id ?? i}
          >
            <TransactionTitleItem
              createdAt={format(date, 'dd/MM/yyyy')}
              name={stakeholder?.fullName}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="TRANSACTION_TYPE" />}
              value={<AppFormattedMessage id={eventFormType[type]} />}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="INVESTMENTS" />}
              value={investedValue?.toLocaleString('en-US')}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="NO_OF_SHARES" />}
              value={(isCompanyShouldHaveOverride
                ? sharesInvested - 8
                : sharesInvested
              ).toLocaleString('en-US')}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={shareClass?.name}
            />
            {filesLinks.length > 0 && (
              <TransactionOverviewItem
                title={<AppFormattedMessage id="DOCUMENTS" />}
                value={
                  <HoverCard>
                    <HoverCardTrigger onClick={() => openFileUploadModal(filesLinks)}>
                      <AddDocumentIcon className="cursor-pointer" />
                    </HoverCardTrigger>

                    <HoverCardContent
                      className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                      side="top"
                    >
                      <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                        {filesLinks.length === 1
                          ? getS3FileOriginalName(filesLinks[0])
                          : `Download ${filesLinks.length} files`}
                      </span>
                    </HoverCardContent>
                  </HoverCard>
                }
              />
            )}
          </div>
        );
      })}
      <PaginationController
        className="mt-6 shrink-0"
        currentPage={currentPage}
        onClick={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
